import type {
  ApiResponse,
  PaginatedResponse,
  PaginationParams,
  TrackerItem,
  TrackerComment,
  TrackerStatusChange,
  TrackerStats,
  TrackerTag,
  TrackerStatus,
  TrackerCategory,
  TrackerPriority,
} from '../types'
import { success, paginated } from './client'
import { delay, generateId, timestamp } from './mockDb'

// ---------------------------------------------------------------------------
// Mock data
// ---------------------------------------------------------------------------

const mockTags: TrackerTag[] = [
  { id: 'tag-1', name: 'docs', color: '#3b82f6' },
  { id: 'tag-2', name: 'ux', color: '#8b5cf6' },
  { id: 'tag-3', name: 'backend', color: '#10b981' },
  { id: 'tag-4', name: 'frontend', color: '#f59e0b' },
  { id: 'tag-5', name: 'security', color: '#ef4444' },
]

const mockItems: TrackerItem[] = [
  {
    id: 'tracker-1',
    title: 'Add dark mode to docs site',
    description: 'Multiple users have requested a dark theme for the documentation site to reduce eye strain.',
    category: 'feature_request',
    priority: 'medium',
    status: 'will_do',
    source: 'doc_feedback',
    createdById: 'user-admin-1',
    createdByName: 'Admin User',
    assignedToId: 'user-dev-1',
    assignedToName: 'Dev User',
    docFeedbackId: 'fb-1',
    sourcePagePath: '/guide/challenges',
    sourcePageUrl: 'https://docs.docurious.com/guide/challenges',
    sourceSubmitterName: 'Jane Doe',
    sourceSubmitterEmail: 'jane@example.com',
    dueDate: null,
    resolvedAt: null,
    tags: [mockTags[0], mockTags[3]],
    commentCount: 3,
    statusChangeCount: 3,
    createdAt: '2026-02-10T10:00:00Z',
    updatedAt: '2026-02-18T14:30:00Z',
  },
  {
    id: 'tracker-2',
    title: 'Fix broken links in API reference',
    description: 'Several links in the API reference section point to non-existent pages.',
    category: 'bug',
    priority: 'high',
    status: 'in_progress',
    source: 'manual',
    createdById: 'user-admin-1',
    createdByName: 'Admin User',
    assignedToId: 'user-admin-1',
    assignedToName: 'Admin User',
    docFeedbackId: null,
    sourcePagePath: null,
    sourcePageUrl: null,
    sourceSubmitterName: null,
    sourceSubmitterEmail: null,
    dueDate: '2026-02-25T00:00:00Z',
    resolvedAt: null,
    tags: [mockTags[0], mockTags[2]],
    commentCount: 1,
    statusChangeCount: 2,
    createdAt: '2026-02-12T09:00:00Z',
    updatedAt: '2026-02-17T11:00:00Z',
  },
  {
    id: 'tracker-3',
    title: 'Clarify XP calculation docs',
    description: 'The XP calculation section is confusing. Need clearer examples and a formula breakdown.',
    category: 'content_fix',
    priority: 'medium',
    status: 'new',
    source: 'doc_feedback',
    createdById: 'user-admin-1',
    createdByName: 'Admin User',
    assignedToId: null,
    assignedToName: null,
    docFeedbackId: 'fb-2',
    sourcePagePath: '/guide/gamification',
    sourcePageUrl: 'https://docs.docurious.com/guide/gamification',
    sourceSubmitterName: 'Alex',
    sourceSubmitterEmail: null,
    dueDate: null,
    resolvedAt: null,
    tags: [mockTags[0]],
    commentCount: 0,
    statusChangeCount: 1,
    createdAt: '2026-02-15T14:00:00Z',
    updatedAt: '2026-02-15T14:00:00Z',
  },
  {
    id: 'tracker-4',
    title: 'Add COPPA compliance documentation',
    description: 'We need to document the COPPA compliance flow for school and parent accounts.',
    category: 'decision',
    priority: 'critical',
    status: 'triaging',
    source: 'manual',
    createdById: 'user-admin-1',
    createdByName: 'Admin User',
    assignedToId: null,
    assignedToName: null,
    docFeedbackId: null,
    sourcePagePath: null,
    sourcePageUrl: null,
    sourceSubmitterName: null,
    sourceSubmitterEmail: null,
    dueDate: null,
    resolvedAt: null,
    tags: [mockTags[4]],
    commentCount: 2,
    statusChangeCount: 2,
    createdAt: '2026-02-16T08:00:00Z',
    updatedAt: '2026-02-19T16:00:00Z',
  },
  {
    id: 'tracker-5',
    title: 'Redesign challenge card component',
    description: 'The challenge card needs a visual refresh to match the new design system.',
    category: 'design',
    priority: 'low',
    status: 'done',
    source: 'manual',
    createdById: 'user-admin-1',
    createdByName: 'Admin User',
    assignedToId: 'user-dev-1',
    assignedToName: 'Dev User',
    docFeedbackId: null,
    sourcePagePath: null,
    sourcePageUrl: null,
    sourceSubmitterName: null,
    sourceSubmitterEmail: null,
    dueDate: '2026-02-14T00:00:00Z',
    resolvedAt: '2026-02-13T17:00:00Z',
    tags: [mockTags[1], mockTags[3]],
    commentCount: 5,
    statusChangeCount: 4,
    createdAt: '2026-02-05T10:00:00Z',
    updatedAt: '2026-02-13T17:00:00Z',
  },
]

const mockStatusChanges: TrackerStatusChange[] = [
  {
    id: 'sc-1',
    trackerItemId: 'tracker-1',
    fromStatus: null,
    toStatus: 'new',
    changedById: 'user-admin-1',
    changedBy: { id: 'user-admin-1', displayName: 'Admin User' },
    reason: 'Promoted from doc feedback on /guide/challenges',
    createdAt: '2026-02-10T10:00:00Z',
  },
  {
    id: 'sc-2',
    trackerItemId: 'tracker-1',
    fromStatus: 'new',
    toStatus: 'triaging',
    changedById: 'user-admin-1',
    changedBy: { id: 'user-admin-1', displayName: 'Admin User' },
    reason: 'Evaluating feasibility and design effort',
    createdAt: '2026-02-12T09:00:00Z',
  },
  {
    id: 'sc-3',
    trackerItemId: 'tracker-1',
    fromStatus: 'triaging',
    toStatus: 'will_do',
    changedById: 'user-admin-1',
    changedBy: { id: 'user-admin-1', displayName: 'Admin User' },
    reason: 'Approved by product team — schedule for next sprint',
    createdAt: '2026-02-14T15:00:00Z',
  },
]

const mockComments: TrackerComment[] = [
  {
    id: 'comment-1',
    trackerItemId: 'tracker-1',
    authorId: 'user-admin-1',
    author: { id: 'user-admin-1', displayName: 'Admin User', avatarUrl: null },
    content: 'This has been requested by 5 different users. Let\'s prioritize it.',
    parentCommentId: null,
    replies: [],
    createdAt: '2026-02-11T10:00:00Z',
    updatedAt: '2026-02-11T10:00:00Z',
  },
]

// ---------------------------------------------------------------------------
// Mock API
// ---------------------------------------------------------------------------

export const trackerApi = {
  async listItems(
    params?: PaginationParams & {
      status?: TrackerStatus
      category?: TrackerCategory
      priority?: TrackerPriority
      search?: string
    }
  ): Promise<PaginatedResponse<TrackerItem>> {
    await delay(200)
    let filtered = [...mockItems]
    if (params?.status && params.status !== ('all' as string)) {
      filtered = filtered.filter((i) => i.status === params.status)
    }
    if (params?.category && params.category !== ('all' as string)) {
      filtered = filtered.filter((i) => i.category === params.category)
    }
    if (params?.priority && params.priority !== ('all' as string)) {
      filtered = filtered.filter((i) => i.priority === params.priority)
    }
    if (params?.search) {
      const s = params.search.toLowerCase()
      filtered = filtered.filter(
        (i) => i.title.toLowerCase().includes(s) || i.description.toLowerCase().includes(s)
      )
    }
    return paginated(filtered, { page: params?.page || 1, limit: params?.limit || 20, total: filtered.length, totalPages: 1, hasMore: false })
  },

  async createItem(data: {
    title: string
    description: string
    category?: TrackerCategory
    priority?: TrackerPriority
  }): Promise<ApiResponse<TrackerItem>> {
    await delay(300)
    const item: TrackerItem = {
      id: generateId(),
      title: data.title,
      description: data.description,
      category: data.category || 'feature_request',
      priority: data.priority || 'medium',
      status: 'new',
      source: 'manual',
      createdById: 'user-admin-1',
      createdByName: 'Admin User',
      assignedToId: null,
      assignedToName: null,
      docFeedbackId: null,
      sourcePagePath: null,
      sourcePageUrl: null,
      sourceSubmitterName: null,
      sourceSubmitterEmail: null,
      dueDate: null,
      resolvedAt: null,
      tags: [],
      commentCount: 0,
      statusChangeCount: 1,
      createdAt: timestamp(),
      updatedAt: timestamp(),
    }
    mockItems.unshift(item)
    return success(item)
  },

  async getItem(id: string): Promise<ApiResponse<TrackerItem>> {
    await delay(150)
    const item = mockItems.find((i) => i.id === id)
    if (!item) return { success: false, data: null as unknown as TrackerItem, error: 'Not found' }
    return success({
      ...item,
      statusChanges: mockStatusChanges.filter((sc) => sc.trackerItemId === id),
    })
  },

  async updateItem(id: string, data: Partial<TrackerItem>): Promise<ApiResponse<TrackerItem>> {
    await delay(200)
    const item = mockItems.find((i) => i.id === id)
    if (!item) return { success: false, data: null as unknown as TrackerItem, error: 'Not found' }
    Object.assign(item, data, { updatedAt: timestamp() })
    return success(item)
  },

  async changeStatus(id: string, status: TrackerStatus, reason?: string): Promise<ApiResponse<TrackerItem>> {
    await delay(200)
    const item = mockItems.find((i) => i.id === id)
    if (!item) return { success: false, data: null as unknown as TrackerItem, error: 'Not found' }
    const fromStatus = item.status
    item.status = status
    item.updatedAt = timestamp()
    if (status === 'done' || status === 'wont_do') item.resolvedAt = timestamp()
    mockStatusChanges.unshift({
      id: generateId(),
      trackerItemId: id,
      fromStatus,
      toStatus: status,
      changedById: 'user-admin-1',
      changedBy: { id: 'user-admin-1', displayName: 'Admin User' },
      reason: reason || null,
      createdAt: timestamp(),
    })
    return success(item)
  },

  async getStatusHistory(id: string): Promise<ApiResponse<TrackerStatusChange[]>> {
    await delay(150)
    return success(mockStatusChanges.filter((sc) => sc.trackerItemId === id))
  },

  async assignItem(id: string, assignedToId: string | null): Promise<ApiResponse<TrackerItem>> {
    await delay(200)
    const item = mockItems.find((i) => i.id === id)
    if (!item) return { success: false, data: null as unknown as TrackerItem, error: 'Not found' }
    item.assignedToId = assignedToId
    item.assignedToName = assignedToId ? 'Assigned User' : null
    return success(item)
  },

  async listComments(id: string): Promise<ApiResponse<TrackerComment[]>> {
    await delay(150)
    return success(mockComments.filter((c) => c.trackerItemId === id))
  },

  async addComment(id: string, content: string, parentCommentId?: string): Promise<ApiResponse<TrackerComment>> {
    await delay(200)
    const comment: TrackerComment = {
      id: generateId(),
      trackerItemId: id,
      authorId: 'user-admin-1',
      author: { id: 'user-admin-1', displayName: 'Admin User', avatarUrl: null },
      content,
      parentCommentId: parentCommentId || null,
      replies: [],
      createdAt: timestamp(),
      updatedAt: timestamp(),
    }
    mockComments.push(comment)
    return success(comment)
  },

  async listTags(): Promise<ApiResponse<TrackerTag[]>> {
    await delay(100)
    return success(mockTags)
  },

  async createTag(name: string, color?: string): Promise<ApiResponse<TrackerTag>> {
    await delay(200)
    const tag: TrackerTag = { id: generateId(), name, color: color || '#6b7280' }
    mockTags.push(tag)
    return success(tag)
  },

  async addTag(itemId: string, tagId: string): Promise<ApiResponse<unknown>> {
    await delay(100)
    const item = mockItems.find((i) => i.id === itemId)
    const tag = mockTags.find((t) => t.id === tagId)
    if (item && tag) item.tags.push(tag)
    return success({ message: 'Tag added' })
  },

  async removeTag(itemId: string, tagId: string): Promise<ApiResponse<unknown>> {
    await delay(100)
    const item = mockItems.find((i) => i.id === itemId)
    if (item) item.tags = item.tags.filter((t) => t.id !== tagId)
    return success({ message: 'Tag removed' })
  },

  async promoteDocFeedback(
    feedbackId: string,
    data?: { title?: string; category?: TrackerCategory; priority?: TrackerPriority }
  ): Promise<ApiResponse<TrackerItem>> {
    await delay(300)
    const item: TrackerItem = {
      id: generateId(),
      title: data?.title || 'Promoted feedback item',
      description: 'Promoted from doc feedback',
      category: data?.category || 'feature_request',
      priority: data?.priority || 'medium',
      status: 'new',
      source: 'doc_feedback',
      createdById: 'user-admin-1',
      createdByName: 'Admin User',
      assignedToId: null,
      assignedToName: null,
      docFeedbackId: feedbackId,
      sourcePagePath: '/guide/unknown',
      sourcePageUrl: 'https://docs.docurious.com/guide/unknown',
      sourceSubmitterName: 'Anonymous',
      sourceSubmitterEmail: null,
      dueDate: null,
      resolvedAt: null,
      tags: [],
      commentCount: 0,
      statusChangeCount: 1,
      createdAt: timestamp(),
      updatedAt: timestamp(),
    }
    mockItems.unshift(item)
    return success(item)
  },

  async getStats(): Promise<ApiResponse<TrackerStats>> {
    await delay(150)
    const byStatus: Partial<Record<TrackerStatus, number>> = {}
    const byCategory: Partial<Record<TrackerCategory, number>> = {}
    const byPriority: Partial<Record<TrackerPriority, number>> = {}
    for (const item of mockItems) {
      byStatus[item.status] = (byStatus[item.status] || 0) + 1
      byCategory[item.category] = (byCategory[item.category] || 0) + 1
      byPriority[item.priority] = (byPriority[item.priority] || 0) + 1
    }
    return success({
      total: mockItems.length,
      byStatus,
      byCategory,
      byPriority,
      createdThisWeek: 3,
      resolvedThisWeek: 1,
    })
  },
}
